package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.TimelineEvent
import com.arenaproplay.admin.ui.components.widgets.CardSpinner
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.mono
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.countryFlag
import kotlin.js.Date
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*

/**
 * Right-side drawer that shows a user's full activity journal, grouped by session.
 * Sessions are sorted newest-first; the newest session is expanded by default.
 *
 * Moved from ui/components/behavior/ → ui/components/admin/ (R9 sweep).
 * Light-era palette replaced with Night Arena tokens.
 */
@Composable
fun UserTimelineDrawer(
    userId: String,
    open: Boolean,
    onClose: () -> Unit,
) {
    val repo = LocalAppContainer.current.adminRepository ?: return

    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var events by remember { mutableStateOf<List<TimelineEvent>?>(null) }
    var retryToken by remember { mutableStateOf(0) }

    val scope = rememberCoroutineScope()

    LaunchedEffect(userId, open, retryToken) {
        if (!open || userId.isBlank()) return@LaunchedEffect
        events = null
        error = null
        loading = true
        try {
            events = repo.getUserTimeline(userId)
        } catch (e: Throwable) {
            error = e.message?.takeIf { it.isNotBlank() } ?: "Failed to load timeline"
        } finally {
            loading = false
        }
    }

    Drawer(open = open, onClose = onClose, title = "Журнал действий") {
        when {
            loading -> {
                Box(
                    Modifier.fillMaxWidth().height(200.px),
                    contentAlignment = Alignment.Center,
                ) {
                    CardSpinner()
                }
            }
            error != null -> {
                ErrorState(
                    message = error!!,
                    onRetry = {
                        scope.launch { retryToken++ }
                    },
                )
            }
            events != null && events!!.isEmpty() -> {
                EmptyState(
                    icon = "📭",
                    title = "Нет событий",
                    hint = "Юзер ещё не был активен или мобильное приложение не шлёт события",
                )
            }
            events != null -> {
                TimelineContent(events!!)
            }
        }
    }
}

@Composable
private fun TimelineContent(events: List<TimelineEvent>) {
    // Group events by sessionId. null sessionId → "Серверные события"
    val grouped: Map<String?, List<TimelineEvent>> = events.groupBy { it.sessionId }

    // Sort sessions newest-first by max occurredAt in each group
    val sortedSessions: List<Pair<String?, List<TimelineEvent>>> = grouped.entries
        .sortedByDescending { (_, evts) -> evts.maxOf { it.occurredAt } }
        .map { (id, evts) -> id to evts }

    // Track which sessions are expanded; newest (index 0) starts expanded
    val expandedSessions = remember(sortedSessions) {
        mutableStateMapOf<String?, Boolean>().also { map ->
            sortedSessions.forEachIndexed { index, (id, _) ->
                map[id] = (index == 0)
            }
        }
    }

    Column(Modifier.fillMaxWidth()) {
        sortedSessions.forEach { (sessionId, sessionEvents) ->
            val isExpanded = expandedSessions[sessionId] ?: false
            SessionGroup(
                sessionId = sessionId,
                events = sessionEvents,
                expanded = isExpanded,
                onToggle = { expandedSessions[sessionId] = !isExpanded },
            )
        }
    }
}

@Composable
private fun SessionGroup(
    sessionId: String?,
    events: List<TimelineEvent>,
    expanded: Boolean,
    onToggle: () -> Unit,
) {
    val sortedEvents = events.sortedBy { it.occurredAt }
    val firstEvent = sortedEvents.first()
    val lastEvent = sortedEvents.last()

    val durationMin = ((lastEvent.occurredAt - firstEvent.occurredAt) / 60_000.0)
    val durationLabel = if (durationMin >= 1.0) {
        val mins = durationMin.toInt()
        " · ${mins}м"
    } else ""

    // Most-common non-null country among the session's events
    val sessionCountry = events.mapNotNull { it.country }
        .groupingBy { it }
        .eachCount()
        .maxByOrNull { it.value }
        ?.key

    val headerLabel = if (sessionId != null) {
        "Сессия ${sessionId.take(8)}"
    } else {
        "Серверные события"
    }

    Column(Modifier.fillMaxWidth()) {
        // Session header — Night Arena glass
        Box(
            Modifier
                .fillMaxWidth()
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("background", "rgba(255,255,255,.04)")
                    property("border-bottom", "1px solid rgba(255,255,255,.08)")
                    property("padding", "12px")
                    property("box-sizing", "border-box")
                }
                .onClick { onToggle() },
        ) {
            Row(
                Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                // Triangle indicator
                SpanText(
                    if (expanded) "▼" else "▶",
                    Modifier.fontSize(10.px).color(AppColors.Muted)
                        .styleModifier { property("margin-right", "8px") },
                )
                Column(Modifier.weight(1f)) {
                    // Session ID / label
                    SpanText(
                        headerLabel,
                        Modifier.bodySMedium().color(AppColors.Ink)
                            .styleModifier { property("font-weight", "600") },
                    )
                    Box(Modifier.height(2.px))
                    // Date + platform + event count + duration + country
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        SpanText(
                            formatEpochMillisLocal(firstEvent.occurredAt),
                            Modifier.bodySMedium().color(AppColors.Muted),
                        )
                        SpanText(
                            " · ${firstEvent.platform} · ${events.size} событий$durationLabel",
                            Modifier.bodySMedium().color(AppColors.Muted),
                        )
                        if (sessionCountry != null) {
                            SpanText(
                                " · ${countryFlag(sessionCountry)} $sessionCountry",
                                Modifier.bodySMedium().color(AppColors.Muted),
                            )
                        }
                    }
                }
            }
        }

        // Event rows (shown when expanded)
        if (expanded) {
            Column(
                Modifier.fillMaxWidth()
                    .styleModifier { property("padding", "4px 0") }
            ) {
                sortedEvents.forEach { event ->
                    TimelineEventRow(event)
                }
            }
        }
    }
}

@Composable
private fun TimelineEventRow(event: TimelineEvent) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 6.px, leftRight = 12.px),
        verticalAlignment = Alignment.Top,
    ) {
        // Time column — HH:mm:ss, 60px wide, mono font
        Box(Modifier.width(60.px).minWidth(60.px)) {
            SpanText(
                formatSessionTime(event.occurredAt),
                Modifier.mono(11).color(AppColors.Faint),
            )
        }
        Box(Modifier.width(8.px))
        // Event name — screens as «Экран: X», actions get a readable label
        SpanText(
            eventLabel(event),
            Modifier.bodyMMedium().color(AppColors.Ink).weight(1f),
        )
        // Screen context for non-screen events (if present)
        if (event.screen != null && !isScreenEvent(event)) {
            Box(Modifier.width(8.px))
            SpanText(
                event.screen,
                Modifier.mono(11).color(AppColors.Primary),
            )
        }
    }
}

private fun isScreenEvent(event: TimelineEvent): Boolean =
    event.eventType.lowercase().let { it == "screen_view" || it == "screen" || it.startsWith("screen.") }

private fun eventLabel(event: TimelineEvent): String {
    val type = event.eventType
    return when {
        isScreenEvent(event) && event.screen != null -> "Экран: ${event.screen}"
        type.startsWith("click.") -> "Действие: ${type.removePrefix("click.")}"
        else -> type
    }
}

/** Format epoch millis as date+time using local browser timezone — matches formatSessionTime rows. */
private fun formatEpochMillisLocal(millis: Long): String {
    val d = Date(millis.toDouble())
    val now = Date()
    val months = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    val h = d.getHours().toString().padStart(2, '0')
    val m = d.getMinutes().toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    val mon = months[d.getMonth()]
    return if (d.getFullYear() == now.getFullYear()) {
        "$day $mon · $h:$m"
    } else {
        "$day $mon ${d.getFullYear()} · $h:$m"
    }
}

private fun formatSessionTime(millis: Long): String {
    val date = Date(millis.toDouble())
    val h = date.getHours().toString().padStart(2, '0')
    val m = date.getMinutes().toString().padStart(2, '0')
    val s = date.getSeconds().toString().padStart(2, '0')
    return "$h:$m:$s"
}
